import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { usePermission } from "../../../features/auth/use-permission";
import { bandSetsQueryOptions } from "../../../features/band-sets/api/band-sets";
import { BandSetWorkspace } from "../../../features/band-sets/components/band-set-workspace";
import { HistoryTabs } from "../../../features/change-history/components/history-tabs";
import { RecordHistory } from "../../../features/change-history/components/record-history";
import { historyTabSearchSchema } from "../../../features/change-history/history-search";

/**
 * `RPM-058`'s band-set editor (`J-292`) with `RPM-059`'s Takes effect control (`J-293`), and the
 * band sets' History (`RPM-063`, `CHUNK-8.md` §3 step 4).
 *
 * Who may publish is read here, from the session's assignments, and handed to the screen: a reader
 * without `bandSet: ["publish"]` (a clinician) opens the same page read-only, and it says so in words
 * (`RPM-057` row 3). The server decides again on every request.
 *
 * ONE HISTORY FOR THE TENANT'S BAND SETS. A change to a band set is a new version, and the server
 * reads every version's publishes from any one of them, so the tab names the current version and
 * offers nothing to choose: each publish is one entry, with its version, its actor and its reason.
 * The tab is rendered only for an account holding `audit: ["read"]`, as on the five J-291 screens.
 */
function BandSetsScreen() {
  const { tab = "details" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const canPublish = usePermission({ bandSet: ["publish"] });
  const canReadHistory = usePermission({ audit: ["read"] });
  const versions = useQuery({ ...bandSetsQueryOptions(), enabled: canReadHistory });
  const current = versions.data?.items.find((v) => v.status === "CURRENT");
  return (
    <div className="flex flex-col gap-4 pt-4">
      <HistoryTabs
        showHistory={canReadHistory}
        tab={tab}
        onTabChange={(next) =>
          void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
        }
        detailsLabel="Band sets"
        details={<BandSetWorkspace canPublish={canPublish} />}
        history={
          <RecordHistory
            entity="band-sets"
            records={
              current === undefined
                ? []
                : [{ id: current.classificationBandSetId, label: "Band sets" }]
            }
            noun="Band set"
            caption={() => "Change history of the tenant's band sets"}
            columnLabels={{
              band_set_version: "Version",
              effective_from: "Takes effect",
              effective_to: "Superseded at",
              supersedes_band_set_id: "Supersedes",
              source_document_ref: "Source document",
            }}
          />
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/band-sets")({
  validateSearch: historyTabSearchSchema,
  component: BandSetsScreen,
  staticData: { screenName: "Band sets" },
});
